import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { format, subYears } from 'date-fns';
import {
  GetColorStatus,
  ELinxHistoryItem,
  withStyles,
  capitalizeFirstLetterOfWords,
  ScheduledTransactionsList,
} from 'vopay';
import { CopyButton, TransactionStatus, Button, Truncate } from 'UI';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import usePostTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import Complete from '@/components/UI/Complete/Complete';
import { Box } from 'vopay';
import styles from './TransactionConfirmationPage.module.scss';
const classes = withStyles(styles);

type ConfirmationType = 'transaction' | 'scheduled' | 'elinx';
type NotificationMethod = 'email' | 'sms' | 'none';

const TransactionConfirmationPage: FunctionComponent = () => {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const location = useLocation();
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionConfirmation' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const {
    type: urlType,
    mode,
    transactionType,
    transactionId,
    currency,
  } = useParams<{
    type: ConfirmationType;
    mode: 'payment' | 'collection';
    transactionType: string;
    transactionId: string;
    currency: string;
  }>();

  // Parse query params
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const type = urlType || (queryParams.get('type') as ConfirmationType) || 'transaction';
  const token = queryParams.get('token') || '';
  const contactID = queryParams.get('ContactID') || '';
  const notificationMethod = (queryParams.get('notificationMethod') as NotificationMethod) || 'none';
  const isElinxScheduled = queryParams.get('scheduled') === 'true';

  // State
  const [contactName, setContactName] = useState<string>('');
  const [paymentMethodInfo, setPaymentMethodInfo] = useState<string>('');
  const [paymentMethodDetails, setPaymentMethodDetails] = useState<string>('');
  const [eLinx, setELinx] = useState<ELinxHistoryItem>();
  const [scheduledTransaction, setScheduledTransaction] = useState<ScheduledTransactionsList>();

  // API Hooks
  const { response } = useClientData();
  const clientAccountsEnabled = response.clientAccountsEnabled;
  const { transactions, fetchTransactionsHistory } = usePostTransactionsHistory();
  const { fetchContact } = useContacts();
  const { fetchClientAccount } = useClientAccounts();
  const { fetchPaymentMethods } = useFetchContactPaymentMethod();
  const [, , , fetchElinxHistory] = useFetchELinxHistory({});
  const [, , , fetchScheduledTransactions] = useFetchScheduledTransactions({});

  // Fetch contact name
  useEffect(() => {
    const fetchContactName = async () => {
      if (!contactID || type === 'elinx') return;

      try {
        if (clientAccountsEnabled) {
          const account = await fetchClientAccount(contactID);
          if (account) {
            const name = account.ClientName
              ? account.ClientName
              : account.BusinessName
                ? account.BusinessName
                : `${account.FirstName || ''} ${account.LastName || ''}`.trim();
            setContactName(name || '');
          }
        } else {
          const contact = await fetchContact(contactID);
          if (contact) {
            setContactName(contact.ContactName || '');
          }
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
      }
    };

    fetchContactName();
  }, [contactID, type, clientAccountsEnabled]);

  // Fetch payment method details
  useEffect(() => {
    const fetchPaymentMethodDetails = async () => {
      if (!token || !contactID || type === 'elinx') return;

      try {
        const resp = await fetchPaymentMethods(contactID);
        const method = resp.PaymentMethods.find((pm) => pm.Token === token);
        if (method) {
          setPaymentMethodInfo(
            tGlobal(`PaymentMethodTypes.${method.PaymentMethod}`, {
              defaultValue: method.PaymentMethod || '',
            }),
          );
          setPaymentMethodDetails(method.PaymentDetails || '');
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };

    fetchPaymentMethodDetails();
  }, [token, contactID]);

  // Fetch transaction data
  useEffect(() => {
    const fetchData = async () => {
      if (type === 'transaction' && transactionId) {
        try {
          await fetchTransactionsHistory({
            StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
            EndDateTime: format(new Date(), 'yyyy-MM-dd'),
            TransactionID: transactionId,
          });
        } catch (error) {
          console.error('Error fetching transaction:', error);
          alertDialog.error(
            t('error', { defaultValue: 'Error' }),
            t('fetchError', { defaultValue: 'Failed to fetch transaction details' }),
          );
        }
      }
    };

    fetchData();
  }, [transactionId, type]);

  // Fetch eLinx data
  useEffect(() => {
    const fetchELinxData = async () => {
      if (type !== 'elinx' || !transactionId) return;

      try {
        const res = await fetchElinxHistory({ ELinxRequestID: transactionId });
        if (res.Success && res.ELinxRequests && res.ELinxRequests.length > 0) {
          setELinx(res.ELinxRequests[0]);
        }
      } catch (error) {
        console.error('Error fetching eLinx:', error);
        alertDialog.error(
          t('error', { defaultValue: 'Error' }),
          t('fetchError', { defaultValue: 'Failed to fetch eLinx details' }),
        );
      }
    };

    fetchELinxData();
  }, [transactionId, type]);

  // Fetch scheduled transaction data
  useEffect(() => {
    const fetchScheduledData = async () => {
      if (type !== 'scheduled' || !transactionId) return;

      try {
        const res = await fetchScheduledTransactions({ ScheduledTransactionID: transactionId });
        if (res.Success && res.ScheduledTransactions) {
          const scheduledTxns = Object.values(res.ScheduledTransactions);
          if (scheduledTxns.length > 0) {
            setScheduledTransaction(scheduledTxns[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching scheduled transaction:', error);
        alertDialog.error(
          t('error', { defaultValue: 'Error' }),
          t('fetchError', { defaultValue: 'Failed to fetch scheduled transaction details' }),
        );
      }
    };

    fetchScheduledData();
  }, [transactionId, type]);

  const currentTransaction = useMemo(() => {
    if (type === 'transaction') {
      return transactions.find((transaction) => transaction.TransactionID === transactionId);
    }
    return undefined;
  }, [transactionId, transactions, type]);

  const transaction = useMemo(() => {
    if (type === 'elinx' && eLinx) {
      return {
        TransactionID: eLinx.ELinxRequestID,
        Amount: eLinx.Amount,
        Currency: eLinx.Currency,
        Status: eLinx.Status,

        RecipientName: eLinx.ReceiverName || 'Recipient',
        Link: eLinx.Link,
      };
    }

    if (type === 'scheduled') {
      return {
        TransactionID: transactionId,
        Amount: scheduledTransaction?.Amount || '0.00',
        Currency: scheduledTransaction?.Currency || currency,
        Status: scheduledTransaction?.Status || 'Scheduled',
        RecipientName: contactName || 'Recipient',
      };
    }

    // Calculate amount from Credit or Debit (one will be '0.00')
    const calculatedAmount =
      currentTransaction?.CreditAmount && currentTransaction.CreditAmount !== '0.00'
        ? currentTransaction.CreditAmount
        : currentTransaction?.DebitAmount || '0.00';

    return {
      TransactionID: transactionId,
      Amount: calculatedAmount,
      Currency: currentTransaction?.Currency || currency,
      Status: currentTransaction?.TransactionStatus || 'Pending',
      RecipientName: contactName || currentTransaction?.FullName || 'Recipient',
    };
  }, [currentTransaction, transactionId, currency, transactionType, type, eLinx, contactName, scheduledTransaction]);

  const notificationMethodMap: Record<NotificationMethod, string> = {
    sms: t('Notification.sms', { defaultValue: 'SMS' }),
    email: t('Notification.email', { defaultValue: 'Email' }),
    none: '',
  };

  const title = useMemo(() => {
    if (type === 'elinx') {
      return mode === 'payment'
        ? t('title.elinxPay', { defaultValue: 'Payment Request Submitted' })
        : t('title.elinxCollect', { defaultValue: 'Collection Request Submitted' });
    }
    return t('title.transaction', { defaultValue: 'Transaction Created' });
  }, [type, mode, t]);

  const actionText = useMemo(() => {
    if (type === 'scheduled' || isElinxScheduled) {
      return t('upcomingAmount', { defaultValue: 'Upcoming Amount' });
    }
    return mode === 'payment'
      ? t('paymentAmount', { defaultValue: 'Payment Amount' })
      : t('collectionAmount', { defaultValue: 'Collection Amount' });
  }, [type, isElinxScheduled, mode, t]);

  const recipientText = useMemo(() => {
    let translationKey: string;
    let defaultValue: string;

    if (type === 'scheduled' || isElinxScheduled) {
      translationKey = mode === 'payment' ? 'TransactionConfirmation.to' : 'TransactionConfirmation.from';
      defaultValue = mode === 'payment' ? 'to <bold>{{name}}</bold>' : 'from <bold>{{name}}</bold>';
    } else {
      translationKey = mode === 'payment' ? 'TransactionConfirmation.payableTo' : 'TransactionConfirmation.collectFrom';
      defaultValue = mode === 'payment' ? 'payable to <bold>{{name}}</bold>' : 'collect from <bold>{{name}}</bold>';
    }

    return (
      <Trans
        ns="translations"
        i18nKey={translationKey}
        defaults={defaultValue}
        values={{ name: transaction.RecipientName }}
        components={{ bold: <strong style={{ color: '#2d2d2d' }} /> }}
      />
    );
  }, [type, isElinxScheduled, mode, transaction?.RecipientName, t]);

  const buttonLabel = useMemo(() => {
    if (type === 'elinx') {
      return mode === 'payment'
        ? t('viewEPayDetails', { defaultValue: 'View ePay Details' })
        : t('viewECollectDetails', { defaultValue: 'View eCollect Details' });
    }
    if (type === 'scheduled') {
      return t('viewScheduleDetails', { defaultValue: 'View Schedule Details' });
    }
    return t('viewDetails', { defaultValue: 'View Transaction Details' });
  }, [type, mode, t]);

  const handleViewDetails = () => {
    if (type === 'elinx') {
      history.replace(`/elinx-history/details/${transactionId}`);
    } else if (type === 'scheduled') {
      history.replace(`/scheduled-transactions/details/${transactionId}`);
    } else {
      history.replace(`/transactions-history/details/${transactionType}/${transactionId}/${currency}/`);
    }
  };

  const descriptionText = useMemo(() => {
    if (type !== 'elinx') return null;
    return mode === 'payment'
      ? t('elinxPaymentProcessingDescription', {
          defaultValue: 'Payment will start processing after this contact provides their preferred payment method.',
        })
      : t('elinxCollectionProcessingDescription', {
          defaultValue: 'Collection will start processing after this contact provides their preferred payment method.',
        });
  }, [type, mode, t]);

  const details = useMemo(() => {
    if (type === 'elinx') {
      return [
        {
          label: t('elinxLink', { defaultValue: 'eLinx Link' }),
          value: (
            <Box gapSize="8px" ai="center" jc="fe">
              <span>
                <Truncate text={transaction.Link || ''} maxLength={20} truncateEnding="..." />
              </span>
              <CopyButton content={transaction.Link || ''} />
            </Box>
          ),
        },
        {
          label: t('elinxId', { defaultValue: 'eLinx ID' }),
          value: (
            <Box gapSize="8px" ai="center" jc="fe">
              <span>{transaction.TransactionID}</span>
              <CopyButton content={transaction.TransactionID} />
            </Box>
          ),
        },
        {
          label: t('status', { defaultValue: 'Status' }),
          value: (
            <Box jc="fe">
              <TransactionStatus
                status={GetColorStatus(transaction.Status)}
                label={tGlobal(`Statuses.${transaction.Status?.toLowerCase()}`, {
                  defaultValue: capitalizeFirstLetterOfWords(transaction.Status || ''),
                })}
              />
            </Box>
          ),
        },
        ...(notificationMethod !== 'none'
          ? [
              {
                label: t('sendNotificationVia', { defaultValue: 'Send Notification via' }),
                value: notificationMethodMap[notificationMethod],
              },
            ]
          : []),
      ];
    }

    if (type === 'scheduled') {
      return [
        {
          label: t('scheduleId', { defaultValue: 'Schedule ID' }),
          value: (
            <Box gapSize="8px" ai="center" jc="fe">
              <span>{transaction.TransactionID}</span>
              <CopyButton content={transaction.TransactionID} />
            </Box>
          ),
        },
        {
          label: t('status', { defaultValue: 'Status' }),
          value: (
            <Box jc="fe">
              <TransactionStatus
                status={GetColorStatus(transaction.Status)}
                label={tGlobal(`Statuses.${transaction.Status?.toLowerCase()}`, {
                  defaultValue: capitalizeFirstLetterOfWords(transaction.Status || ''),
                })}
              />
            </Box>
          ),
        },
        ...(paymentMethodInfo
          ? [
              {
                label: t('paymentMethod', { defaultValue: 'Payment Method' }),
                value: paymentMethodInfo,
              },
            ]
          : []),
        ...(paymentMethodDetails
          ? [
              {
                label: t('paymentMethodDetails', { defaultValue: 'Payment Method Details' }),
                value: paymentMethodDetails,
              },
            ]
          : []),
      ];
    }

    // Transaction type
    return [
      {
        label: t('transactionId', { defaultValue: 'Transaction ID' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>{transaction.TransactionID}</span>
            <CopyButton content={transaction.TransactionID} />
          </Box>
        ),
      },
      {
        label: t('status', { defaultValue: 'Status' }),
        value: (
          <Box jc="fe">
            <TransactionStatus
              status={GetColorStatus(transaction.Status)}
              label={tGlobal(`Statuses.${transaction.Status?.toLowerCase()}`, {
                defaultValue: capitalizeFirstLetterOfWords(transaction.Status || ''),
              })}
            />
          </Box>
        ),
      },
      ...(paymentMethodInfo
        ? [
            {
              label: t('paymentMethod', { defaultValue: 'Payment Method' }),
              value: paymentMethodInfo,
            },
          ]
        : []),
      ...(paymentMethodDetails
        ? [
            {
              label: t('paymentMethodDetails', { defaultValue: 'Payment Method Details' }),
              value: paymentMethodDetails,
            },
          ]
        : []),
    ];
  }, [transaction, t, type, paymentMethodInfo, paymentMethodDetails, notificationMethod]);

  return (
    <Complete
      title={title}
      amount={`${transaction.Currency} ${transaction.Amount}`}
      actionText={actionText}
      recipientText={recipientText}
      showAmounts={true}
      details={details}
      description={type === 'elinx' ? descriptionText : undefined}
      className={classes('container')}
    >
      <div className={classes('buttonContainer')}>
        <Button size="small" onClick={handleViewDetails}>
          {buttonLabel}
        </Button>
        <Button
          size="small"
          colorScheme="primary"
          onClick={() => {
            const path = mode === 'payment' ? '/single-payment-new' : '/single-collection-new';
            history.replace(path);
          }}
        >
          {mode === 'payment'
            ? t('createNewPayment', { defaultValue: 'Create New Payment' })
            : t('createNewCollection', { defaultValue: 'Create New Collection' })}
        </Button>
      </div>
    </Complete>
  );
};

export default TransactionConfirmationPage;
